import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Check, AlertTriangle, X, Download, ChevronRight, ArrowLeft } from "lucide-react";
import { useConfig } from "@/hooks/useConfig";
import * as XLSX from "xlsx";
import { fmtDate, istToday } from "@/lib/dateFormat";

const fmt = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const STATUS_CHIP = { matched: "bg-emerald-100 text-emerald-800", warning: "bg-amber-100 text-amber-800", error: "bg-red-100 text-red-800" };

export default function FreightUploadPage() {
  const { getValues } = useConfig();
  const gstRates = getValues("logistics_gst_rates");
  const [activeTab, setActiveTab] = useState("upload");

  // Step tracking
  const [step, setStep] = useState(1);
  const [unmatchedRows, setUnmatchedRows] = useState([]);
  const [pendingRows, setPendingRows] = useState([]);

  // Step 1 — Header
  const [vendors, setVendors] = useState([]);
  const [header, setHeader] = useState({
    vendor_code: "", vendor_name: "", vendor_invoice_number: "", vendor_invoice_date: "",
    invoice_period: "", taxable_amount: "", gst_rate: "18", gst_amount: "", rounding: "0",
    invoice_total: "", vendor_pdf_document_id: "",
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [dupWarning, setDupWarning] = useState(null);

  // Step 2 — Parse
  const [parseResult, setParseResult] = useState(null);
  const [filterBucket, setFilterBucket] = useState("all");
  const [saving, setSaving] = useState(false);

  // Step 3 — Commit result
  const [commitResult, setCommitResult] = useState(null);

  // History
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/freight/vendors").then(r => setVendors(r.data || [])).catch(() => {});
    api.get("/freight/history").then(r => setHistory(r.data || [])).catch(() => {});
  }, []);

  // Auto-calc GST and total
  useEffect(() => {
    const taxable = parseFloat(header.taxable_amount) || 0;
    const rate = parseFloat(header.gst_rate) || 0;
    const rounding = parseFloat(header.rounding) || 0;
    const gst = Math.round(taxable * rate / 100 * 100) / 100;
    const total = taxable + gst + rounding;
    setHeader(h => ({ ...h, gst_amount: gst.toFixed(2), invoice_total: total.toFixed(2) }));
  }, [header.taxable_amount, header.gst_rate, header.rounding]);

  // Check duplicate
  const checkDuplicate = async (inv_num) => {
    if (!inv_num) { setDupWarning(null); return; }
    try {
      const { data } = await api.post("/freight/check-duplicate", { vendor_invoice_number: inv_num, vendor_code: header.vendor_code });
      setDupWarning(data.exists ? data : null);
    } catch { setDupWarning(null); }
  };

  // Step 1 → Step 2: upload PDF then move
  const handleStep1Next = async () => {
    if (!header.vendor_code || !header.vendor_invoice_number || !header.vendor_invoice_date || !header.taxable_amount) {
      toast.error("Fill all required fields"); return;
    }
    // Upload PDF if provided
    if (pdfFile) {
      try {
        const fd = new FormData();
        fd.append("file", pdfFile);
        fd.append("entity_type", "freight_vendor_invoice");
        fd.append("entity_id", header.vendor_invoice_number);
        fd.append("document_type", "freight_vendor_invoice");
        const { data: docResp } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setHeader(h => ({ ...h, vendor_pdf_document_id: docResp._id || docResp.document_id || "" }));
      } catch (e) { toast.error("PDF upload failed: " + formatApiError(e.response?.data?.detail)); return; }
    }
    setStep(2);
  };

  // Step 2: Parse Excel file
  const handleFileUpload = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (json.length < 2) { toast.error("File has no data rows"); setSaving(false); return; }
      const headers = json[0].map(h => String(h).trim());
      const rows = json.slice(1);
      const { data } = await api.post("/freight/parse", { vendor_code: header.vendor_code, headers, rows });
      setParseResult(data);
      toast.success(`Parsed: ${data.matched?.length || 0} matched, ${data.warnings?.length || 0} need a decision, ${data.unmatched?.length || 0} unmatched, ${data.errors?.length || 0} errors`);
    } catch (e) { toast.error("Parse failed: " + formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  // Skip error row
  const skipErrorRow = (idx) => {
    if (!parseResult) return;
    const errors = [...parseResult.errors];
    errors.splice(idx, 1);
    setParseResult({ ...parseResult, errors });
  };

  // BATCH 4 (4.3d): freight already on the order is never overwritten silently.
  const setExistingMode = (idx, mode) => {
    if (!parseResult) return;
    const warnings = [...parseResult.warnings];
    warnings[idx] = { ...warnings[idx], existing_mode: mode };
    setParseResult({ ...parseResult, warnings });
  };

  const approveWarning = (idx) => {
    if (!parseResult) return;
    const warnings = [...parseResult.warnings];
    const row = { ...warnings[idx], status: "matched" };
    warnings.splice(idx, 1);
    setParseResult({ ...parseResult, warnings, matched: [...(parseResult.matched || []), row] });
  };

  const loadUnmatchedAndPending = async () => {
    try {
      const [u, p] = await Promise.all([api.get("/freight/unmatched"), api.get("/freight/pending")]);
      setUnmatchedRows(u.data?.data || []);
      setPendingRows(p.data?.data || []);
    } catch { /* permission handled by the API */ }
  };

  const linkUnmatched = async (rowId, orderNumber, isRto) => {
    const match = (pendingRows || []).find(p => p.order_number === orderNumber) || null;
    if (!orderNumber) { toast.error("Type the ORBIT order number to link"); return; }
    try {
      const { data: found } = await api.get("/orders", { params: { search: orderNumber, limit: 1 } });
      const orderId = match?.order_id || found?.orders?.[0]?._id || found?.[0]?._id;
      if (!orderId) { toast.error("Order not found"); return; }
      await api.post(`/freight/unmatched/${rowId}/link`, { order_id: orderId, is_rto: isRto, mode: "add" });
      toast.success(`Linked to ${orderNumber}`);
      loadUnmatchedAndPending();
    } catch (e) { toast.error("Link failed: " + formatApiError(e.response?.data?.detail)); }
  };

  const approveAllWarnings = () => {
    if (!parseResult || !parseResult.warnings?.length) return;
    const approved = parseResult.warnings.map(w => ({ ...w, status: "matched" }));
    setParseResult({ ...parseResult, warnings: [], matched: [...(parseResult.matched || []), ...approved] });
  };

  const exportParseRows = (label, rows) => {
    const ws = XLSX.utils.json_to_sheet(rows.map(r => ({
      "Status": r.is_rto ? "RTO" : r.status,
      "Invoice Number": r.invoice_number || "",
      "Docket Number": r.docket_number || "",
      "Consignee": r.consignee || "",
      "Destination": r.destination || "",
      "Chargeable Weight (kg)": r.chargeable_weight_kg || "",
      "Final Price (Rs)": r.final_price || 0,
      "ORBIT Order": r.matched_order ? r.matched_order.order_number : "Not found",
      "Warning/Error Reason": r.error_reason || "",
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, label);
    const fname = `Freight_Upload_${label}_${(header.vendor_invoice_number || "").replace(/[/\\]/g, "-")}_${istToday()}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success(`Exported ${rows.length} ${label} rows`);
  };

  // Step 3: Commit
  const handleCommit = async () => {
    if (!parseResult) return;
    setSaving(true);
    const rowsToCommit = [...(parseResult.matched || []), ...(parseResult.warnings || []).filter(w => w.matched_order)];
    const undecided = rowsToCommit.filter(r => r.existing_freight_rupees && !r.existing_mode);
    if (undecided.length) {
      toast.error(`${undecided.length} order(s) already have freight — choose Add, Replace or Skip first`);
      setSaving(false);
      return;
    }
    try {
      const { data } = await api.post("/freight/commit", {
        header,
        rows: rowsToCommit,
        total_rows: parseResult.total_rows,
        rows_matched: parseResult.matched?.length || 0,
        rows_warnings: parseResult.warnings?.length || 0,
        rows_errors: parseResult.errors?.length || 0,
        rows_ecom_skipped: parseResult.ecom_skipped || 0,
        pending_errors: parseResult.errors || [],
        unmatched: parseResult.unmatched || [],
        replace: !!dupWarning,
      });
      setCommitResult(data);
      setStep(3);
      toast.success(`Freight committed for ${data.committed_count} orders`);
      // Refresh history
      api.get("/freight/history").then(r => setHistory(r.data || [])).catch(() => {});
    } catch (e) { toast.error("Commit failed: " + formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const allRows = parseResult ? [...(parseResult.matched || []), ...(parseResult.warnings || []), ...(parseResult.unmatched || []), ...(parseResult.errors || [])] : [];
  const filteredRows = filterBucket === "all" ? allRows : allRows.filter(r => r.status === filterBucket);

  return (
    <div className="space-y-4" data-testid="freight-upload-page">
      <h1 className="text-2xl font-bold tracking-tight">Upload Freight</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="upload">Upload Wizard</TabsTrigger>
          <TabsTrigger value="history">Upload History ({history.length})</TabsTrigger>
          <TabsTrigger value="unmatched" data-testid="freight-unmatched-tab" onClick={loadUnmatchedAndPending}>Unmatched ({unmatchedRows.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="freight-pending-tab" onClick={loadUnmatchedAndPending}>Freight Pending ({pendingRows.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="unmatched">
          <div className="border border-zinc-200 rounded-sm overflow-auto" data-testid="freight-unmatched-table">
            <table className="data-table w-full">
              <thead><tr><th>Vendor invoice</th><th>Invoice no. in row</th><th>Docket</th><th>Consignee</th><th className="num">Amount</th><th>Reason</th><th>Link to order</th></tr></thead>
              <tbody>
                {unmatchedRows.length === 0 ? <tr><td colSpan={7} className="text-center py-6 text-xs text-zinc-400">No unmatched rows</td></tr> :
                unmatchedRows.map(r => (
                  <tr key={r._id}>
                    <td className="text-[10px] font-mono">{r.vendor_invoice_number}</td>
                    <td className="text-[10px] font-mono">{r.row?.invoice_number || "—"}</td>
                    <td className="text-[10px] font-mono">{r.row?.docket_number || "—"}</td>
                    <td className="text-[10px]">{r.row?.consignee || "—"}</td>
                    <td className="num font-mono text-xs">{(r.row?.final_price || 0).toFixed(2)}</td>
                    <td className="text-[10px] text-red-600">{r.reason}</td>
                    <td>
                      <div className="flex gap-1 items-center">
                        <Input placeholder="ORBIT order no." className="h-7 text-[10px] w-32" data-testid={`link-order-input-${r._id}`}
                               onChange={e => { r._typed = e.target.value; }} />
                        <Button className="h-7 text-[10px] rounded-sm" data-testid={`link-order-btn-${r._id}`}
                                onClick={() => linkUnmatched(r._id, r._typed, !!r.row?.is_rto)}>Link</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="pending">
          <div className="border border-zinc-200 rounded-sm overflow-auto" data-testid="freight-pending-table">
            <table className="data-table w-full">
              <thead><tr><th>Order</th><th>Customer</th><th>Channel</th><th>Status</th><th>Dispatched</th><th className="num">Age (days)</th><th className="num">Estimated</th><th>Escalated</th></tr></thead>
              <tbody>
                {pendingRows.length === 0 ? <tr><td colSpan={8} className="text-center py-6 text-xs text-zinc-400">Every dispatched order has actual freight</td></tr> :
                pendingRows.slice(0, 300).map(r => (
                  <tr key={r.order_id} data-testid={`freight-pending-row-${r.order_number}`}>
                    <td className="text-xs font-mono">{r.order_number}</td>
                    <td className="text-xs">{r.customer_name}</td>
                    <td className="text-[10px]">{r.channel_name}</td>
                    <td className="text-[10px]">{r.status}</td>
                    <td className="text-[10px] font-mono">{fmtDate(r.dispatch_date)}</td>
                    <td className="num font-mono text-xs">{r.age_days}</td>
                    <td className="num font-mono text-xs">{(r.estimated_freight || 0).toFixed(2)}</td>
                    <td className="text-[10px]">{r.escalate ? <span className="text-red-600 font-semibold">Escalated</span> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="upload">
          {/* Progress */}
          <div className="flex items-center gap-2 mb-4 text-xs">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex items-center gap-1 px-3 py-1 rounded-full ${step >= s ? "bg-blue-600 text-white" : "bg-zinc-100 text-zinc-400"}`}>
                {s}. {s === 1 ? "Vendor Invoice" : s === 2 ? "Upload & Review" : "Confirm"}
              </div>
            ))}
          </div>

          {/* STEP 1 */}
          {step === 1 && (
            <div className="border border-zinc-200 p-4 rounded-sm space-y-4" data-testid="step1-vendor-header">
              <h2 className="text-sm font-semibold">Step 1 — Vendor Invoice Header</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Vendor *</Label>
                  <select value={header.vendor_code} onChange={e => { const v = vendors.find(v => v.vendor_code === e.target.value); setHeader(h => ({ ...h, vendor_code: e.target.value, vendor_name: v?.vendor_name || "" })); }} className="mt-1 w-full h-8 text-xs border border-zinc-200 rounded-sm px-2" data-testid="vendor-select">
                    <option value="">-- Select vendor --</option>
                    {vendors.map(v => <option key={v.vendor_code} value={v.vendor_code}>{v.vendor_name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Vendor Invoice # *</Label>
                  <Input value={header.vendor_invoice_number} onChange={e => { setHeader(h => ({ ...h, vendor_invoice_number: e.target.value })); checkDuplicate(e.target.value); }} className="mt-1 h-8 text-xs" data-testid="vendor-invoice-num" />
                  {dupWarning && <p className="text-[10px] text-amber-600 mt-1">Invoice uploaded on {fmtDate(dupWarning.uploaded_at)} by {dupWarning.uploaded_by}. Will replace.</p>}
                </div>
                <div><Label className="text-xs">Invoice Date *</Label><Input type="date" value={fmtDate(header.vendor_invoice_date)} onChange={e => setHeader(h => ({ ...h, vendor_invoice_date: e.target.value }))} className="mt-1 h-8 text-xs" /></div>
                <div><Label className="text-xs">Invoice Period</Label><Input value={header.invoice_period} onChange={e => setHeader(h => ({ ...h, invoice_period: e.target.value }))} className="mt-1 h-8 text-xs" placeholder="e.g. April 2026" /></div>
                <div><Label className="text-xs">Taxable Amount (Rs) *</Label><Input type="number" step="0.01" value={header.taxable_amount} onChange={e => setHeader(h => ({ ...h, taxable_amount: e.target.value }))} className="mt-1 h-8 text-xs font-mono" data-testid="taxable-amount" /></div>
                <div>
                  <Label className="text-xs">GST Rate %</Label>
                  <select value={header.gst_rate} onChange={e => setHeader(h => ({ ...h, gst_rate: e.target.value }))} className="mt-1 w-full h-8 text-xs border border-zinc-200 rounded-sm px-2" data-testid="gst-rate-select">
                    {(gstRates.length > 0 ? gstRates : ["0", "5", "12", "18", "28"]).map(r => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
                <div><Label className="text-xs">GST Amount (Rs)</Label><Input type="number" step="0.01" value={header.gst_amount} onChange={e => setHeader(h => ({ ...h, gst_amount: e.target.value }))} className="mt-1 h-8 text-xs font-mono" /></div>
                <div><Label className="text-xs">Rounding (Rs)</Label><Input type="number" step="0.01" value={header.rounding} onChange={e => setHeader(h => ({ ...h, rounding: e.target.value }))} className="mt-1 h-8 text-xs font-mono" /></div>
                <div><Label className="text-xs">Invoice Total (Rs)</Label><Input value={header.invoice_total} readOnly className="mt-1 h-8 text-xs font-mono bg-zinc-50 font-bold" /></div>
              </div>
              <div>
                <Label className="text-xs">Vendor Invoice PDF</Label>
                <Input type="file" accept=".pdf" onChange={e => setPdfFile(e.target.files?.[0] || null)} className="mt-1 h-8 text-xs" data-testid="vendor-pdf" />
              </div>
              <Button onClick={handleStep1Next} className="h-8 text-xs" data-testid="step1-next">Next — Upload Annexure <ChevronRight className="w-3 h-3 ml-1" /></Button>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-4" data-testid="step2-parse">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Step 2 — Upload Annexure & Review</h2>
                <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => setStep(1)}><ArrowLeft className="w-3 h-3 mr-1" />Back to Step 1</Button>
              </div>
              <div className="border-2 border-dashed border-zinc-300 rounded-sm p-6 text-center" data-testid="file-dropzone">
                <Upload className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
                <p className="text-xs text-zinc-500 mb-2">Upload GATE freight annexure (.xlsx, .csv)</p>
                <Input type="file" accept=".xlsx,.xls,.csv" onChange={e => handleFileUpload(e.target.files?.[0])} className="max-w-xs mx-auto h-8 text-xs" data-testid="annexure-upload" />
              </div>

              {saving && <p className="text-xs text-zinc-400 text-center">Parsing file...</p>}

              {parseResult && (
                <>
                  {/* Info Banner */}
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm text-xs" data-testid="parse-banner">
                    <strong>{parseResult.total_rows}</strong> rows read · <span className="text-emerald-700"><strong>{parseResult.matched?.length || 0}</strong> matched</span> · <span className="text-amber-700"><strong>{parseResult.warnings?.length || 0}</strong> warnings</span> · <span className="text-red-700"><strong>{parseResult.errors?.length || 0}</strong> errors</span>{parseResult.ecom_skipped > 0 && <span className="text-zinc-500"> · {parseResult.ecom_skipped} ecom skipped</span>} · Total freight <strong>Rs {fmt(parseResult.total_freight_rupees)}</strong>
                  </div>

                  {/* Bucket summary cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="border border-emerald-200 bg-emerald-50 p-2 rounded-sm text-center cursor-pointer" onClick={() => setFilterBucket("matched")}>
                      <p className="text-[9px] text-emerald-600 uppercase">Matched</p>
                      <p className="font-bold text-emerald-700">{parseResult.matched?.length || 0}</p>
                      <p className="text-[9px] text-emerald-500">Rs {fmt(parseResult.matched_freight_rupees)}</p>
                    </div>
                    <div className="border border-amber-200 bg-amber-50 p-2 rounded-sm text-center cursor-pointer" onClick={() => setFilterBucket("warning")}>
                      <p className="text-[9px] text-amber-600 uppercase">Warnings</p>
                      <p className="font-bold text-amber-700">{parseResult.warnings?.length || 0}</p>
                    </div>
                    <div className="border border-red-200 bg-red-50 p-2 rounded-sm text-center cursor-pointer" onClick={() => setFilterBucket("error")}>
                      <p className="text-[9px] text-red-600 uppercase">Errors</p>
                      <p className="font-bold text-red-700">{parseResult.errors?.length || 0}</p>
                    </div>
                  </div>

                  {/* Filter chips + Export buttons */}
                  <div className="flex gap-1 items-center flex-wrap">
                    {["all", "matched", "warning", "error"].map(b => (
                      <Button key={b} variant={filterBucket === b ? "default" : "outline"} size="sm" className="h-6 text-[10px]" onClick={() => setFilterBucket(b)}>{b === "all" ? "All" : b.charAt(0).toUpperCase() + b.slice(1)}</Button>
                    ))}
                    {(parseResult.warnings?.length || 0) > 0 && (
                      <Button variant="outline" size="sm" className="h-6 text-[10px] ml-2 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={approveAllWarnings} data-testid="approve-all-warnings">Approve All {parseResult.warnings.length} Warnings</Button>
                    )}
                    <span className="mx-1 text-zinc-300">|</span>
                    <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => exportParseRows("All", allRows)}><Download className="w-3 h-3 mr-1" />All</Button>
                    <Button variant="outline" size="sm" className="h-6 text-[10px] border-emerald-300 text-emerald-700" onClick={() => exportParseRows("Matched", parseResult.matched || [])}><Download className="w-3 h-3 mr-1" />Matched</Button>
                    <Button variant="outline" size="sm" className="h-6 text-[10px] border-amber-300 text-amber-700" onClick={() => exportParseRows("Warnings", parseResult.warnings || [])}><Download className="w-3 h-3 mr-1" />Warnings</Button>
                    <Button variant="outline" size="sm" className="h-6 text-[10px] border-red-300 text-red-700" onClick={() => exportParseRows("Errors", parseResult.errors || [])}><Download className="w-3 h-3 mr-1" />Errors</Button>
                  </div>

                  {/* Table */}
                  <div className="border rounded-lg overflow-auto max-h-[400px]" data-testid="parse-table">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-zinc-50 text-[10px]">
                          <TableHead className="w-16">Status</TableHead>
                          <TableHead className="w-32">Invoice #</TableHead>
                          <TableHead className="w-24">Docket</TableHead>
                          <TableHead>Consignee</TableHead>
                          <TableHead className="w-24">Destination</TableHead>
                          <TableHead className="text-right w-16">CW (kg)</TableHead>
                          <TableHead className="text-right w-20">Final Price</TableHead>
                          <TableHead className="w-28">ORBIT Order</TableHead>
                          <TableHead className="w-20">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRows.map((r, i) => (
                          <TableRow key={i} className={`text-[11px] ${r.status === "error" ? "bg-red-50/50" : r.status === "warning" ? "bg-amber-50/50" : ""}`}>
                            <TableCell><span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_CHIP[r.status]}`}>{r.is_rto ? "RTO" : r.status}</span></TableCell>
                            <TableCell className="font-mono text-[10px]">{r.invoice_number || "—"}</TableCell>
                            <TableCell className="text-[10px]">{r.docket_number || "—"}</TableCell>
                            <TableCell className="truncate max-w-[120px]">{r.consignee}</TableCell>
                            <TableCell className="text-[10px]">{r.destination}</TableCell>
                            <TableCell className="text-right font-mono">{r.chargeable_weight_kg || "—"}</TableCell>
                            <TableCell className="text-right font-mono font-semibold">{fmt(r.final_price)}</TableCell>
                            <TableCell className="text-[10px]">{r.matched_order ? <span className="text-emerald-700 font-medium">{r.matched_order.order_number}</span> : <span className="text-zinc-400">{r.error_reason}</span>}</TableCell>
                            <TableCell>
                              {r.status === "error" && <Button variant="ghost" size="sm" className="h-5 text-[9px] px-1" onClick={() => skipErrorRow(parseResult.errors.indexOf(r))}>Skip</Button>}
                              {r.status === "warning" && r.matched_order && !r.existing_freight_rupees && <Button variant="ghost" size="sm" className="h-5 text-[9px] px-1 text-amber-700" onClick={() => approveWarning(parseResult.warnings.indexOf(r))}>Approve</Button>}
                              {/* BATCH 4 (4.3d): the order already has freight — the uploader decides */}
                              {r.status === "warning" && r.existing_freight_rupees ? (
                                <div className="flex gap-0.5" data-testid={`existing-choice-${r.matched_order?.order_number}`}>
                                  {["add", "replace", "skip"].map(m => (
                                    <Button key={m} variant={r.existing_mode === m ? "default" : "ghost"} size="sm"
                                            className={`h-5 text-[9px] px-1 ${r.existing_mode === m ? "bg-zinc-900 text-white" : ""}`}
                                            data-testid={`existing-${m}-${r.matched_order?.order_number}`}
                                            onClick={() => setExistingMode(parseResult.warnings.indexOf(r), m)}>
                                      {m === "add" ? "Add" : m === "replace" ? "Replace" : "Skip"}
                                    </Button>
                                  ))}
                                </div>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Commit button */}
                  <div className="flex justify-end">
                    <Button onClick={() => setStep(3)} className="h-8 text-xs" data-testid="step2-review">Review & Commit <ChevronRight className="w-3 h-3 ml-1" /></Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && !commitResult && parseResult && (
            <div className="space-y-4" data-testid="step3-confirm">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Step 3 — Confirm & Commit</h2>
                <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => setStep(2)}><ArrowLeft className="w-3 h-3 mr-1" />Back</Button>
              </div>

              <div className="border border-zinc-200 p-4 rounded-sm space-y-3">
                <h3 className="text-xs font-semibold text-zinc-600">Invoice Summary</h3>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-zinc-400">Vendor:</span> {header.vendor_name}</div>
                  <div><span className="text-zinc-400">Invoice #:</span> {header.vendor_invoice_number}</div>
                  <div><span className="text-zinc-400">Date:</span> {fmtDate(header.vendor_invoice_date)}</div>
                  <div><span className="text-zinc-400">Period:</span> {header.invoice_period}</div>
                  <div><span className="text-zinc-400">Taxable:</span> Rs {fmt(parseFloat(header.taxable_amount) || 0)}</div>
                  <div><span className="text-zinc-400">Total:</span> <strong>Rs {fmt(parseFloat(header.invoice_total) || 0)}</strong></div>
                </div>

                <h3 className="text-xs font-semibold text-zinc-600 mt-3">Impact Preview</h3>
                <div className="text-xs space-y-1">
                  <p className="text-emerald-700"><Check className="w-3 h-3 inline mr-1" /><strong>{(parseResult.matched?.length || 0) + (parseResult.warnings?.filter(w => w.matched_order)?.length || 0)}</strong> orders will have actual freight saved (Rs {fmt(parseResult.matched_freight_rupees)})</p>
                  <p className="text-blue-700"><Check className="w-3 h-3 inline mr-1" />PnL switches from estimated to actual for these orders</p>
                  {(parseResult.errors?.length || 0) > 0 && <p className="text-amber-600"><AlertTriangle className="w-3 h-3 inline mr-1" />{parseResult.errors.length} rows remain unresolved</p>}
                </div>

                <p className="text-[10px] text-zinc-400 mt-2">Edits allowed for 15 days by logistics team, 60 days by Finance/Admin.</p>
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="confirm-check" className="w-3.5 h-3.5 accent-blue-600" />
                <Label htmlFor="confirm-check" className="text-xs cursor-pointer">I confirm the vendor invoice matches the uploaded annexure</Label>
              </div>

              <Button onClick={handleCommit} disabled={saving} className="h-8 text-xs" data-testid="commit-btn">
                {saving ? "Committing..." : `Commit ${(parseResult.matched?.length || 0)} matched orders`}
              </Button>
            </div>
          )}

          {/* STEP 3 — Result */}
          {step === 3 && commitResult && (
            <div className="border border-emerald-200 bg-emerald-50 p-6 rounded-sm text-center" data-testid="commit-success">
              <Check className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
              <h2 className="text-lg font-semibold text-emerald-800">Freight Committed Successfully</h2>
              <p className="text-sm text-emerald-600 mt-1">{commitResult.committed_count} orders updated · Rs {fmt(commitResult.total_freight_paise / 100)} total freight</p>
              <p className="text-xs text-zinc-500 mt-2">Batch ID: {commitResult.batch_id}</p>
              <div className="flex gap-2 justify-center mt-4">
                <Button variant="outline" size="sm" className="text-xs" onClick={() => { setStep(1); setParseResult(null); setCommitResult(null); setHeader(h => ({ ...h, vendor_invoice_number: "", taxable_amount: "", rounding: "0" })); }}>Upload Another Invoice</Button>
                <Button variant="outline" size="sm" className="text-xs" onClick={() => setActiveTab("history")}>View History</Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history">
          <div className="space-y-3" data-testid="upload-history">
            <h2 className="text-sm font-semibold">Upload History</h2>
            {history.length === 0 ? <p className="text-xs text-zinc-400 py-4 text-center">No uploads yet</p> : (
              <div className="border rounded-lg overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50 text-[10px]">
                      <TableHead>Vendor</TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead>By</TableHead>
                      <TableHead className="text-right">Total (Rs)</TableHead>
                      <TableHead className="text-center">Matched</TableHead>
                      <TableHead className="text-center">Errors</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((b, i) => (
                      <TableRow key={i} className={`text-[11px] ${b.status === "superseded" ? "opacity-40" : ""}`}>
                        <TableCell>{b.vendor_code}</TableCell>
                        <TableCell className="font-mono">{b.vendor_invoice_number}</TableCell>
                        <TableCell>{b.invoice_period}</TableCell>
                        <TableCell>{(b.uploaded_at || "").slice(0, 10)}</TableCell>
                        <TableCell>{b.uploaded_by_name}</TableCell>
                        <TableCell className="text-right font-mono">{fmt((b.total_paise || 0) / 100)}</TableCell>
                        <TableCell className="text-center">{b.rows_committed || 0}</TableCell>
                        <TableCell className="text-center">{b.rows_errors || 0}</TableCell>
                        <TableCell><Badge variant="outline" className={`text-[9px] ${b.status === "completed" ? "border-emerald-300 text-emerald-700" : b.status === "superseded" ? "border-zinc-300" : "border-amber-300 text-amber-700"}`}>{b.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
